"use client";
import React from "react";
import { motion } from "motion/react";
import { Factory, Users, Zap, CheckCircle, ArrowRight } from "lucide-react";
// import { GlassCard } from "../components/GlassCard";
// import { ImageWithFallback } from "../components/figma/ImageWithFallback";
// import { useLocalization } from "../context/LocalizationContext";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import { GlassCard } from "@/src/components/GlassCard";

export const ManufacturingPage: React.FC = () => {
  const { t } = useTranslation("common");

  const steps = [
    {
      icon: Factory,
      title: "Design & Prototyping",
      description:
        "Our design team works closely with you to create custom packaging that aligns with your brand. We develop prototypes and samples for your approval.",
      details: [
        "Initial consultation and requirements gathering",
        "Custom design concepts and mockups",
        "3D prototype development",
        "Sample production and approval",
      ],
    },
    {
      icon: Users,
      title: "Material Selection",
      description:
        "Choose from a wide range of sustainable, high-quality materials based on your product requirements and budget.",
      details: [
        "Material recommendation based on product needs",
        "Sustainability and compliance assessment",
        "Cost optimization analysis",
        "Material sourcing and procurement",
      ],
    },
    {
      icon: Zap,
      title: "Production",
      description:
        "State-of-the-art manufacturing equipment and skilled technicians ensure precision and quality in every unit produced.",
      details: [
        "Automated cutting and die-cutting",
        "High-speed digital and offset printing",
        "Custom assembly and finishing",
        "Real-time production monitoring",
      ],
    },
    {
      icon: CheckCircle,
      title: "Quality Testing",
      description:
        "Rigorous quality control at every stage ensures your packaging meets the highest standards and specifications.",
      details: [
        "Dimensional accuracy checks",
        "Print quality inspection",
        "Structural integrity testing",
        "Final QC before shipment",
      ],
    },
    {
      icon: ArrowRight,
      title: "Delivery & Logistics",
      description:
        "Efficient logistics and reliable delivery ensure your packaging arrives on time and in perfect condition.",
      details: [
        "Secure packaging for transport",
        "Flexible delivery options",
        "Real-time tracking",
        "Dedicated customer support",
      ],
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1635961179148-3d886568775c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwcGFja2FnaW5nJTIwZmFjdG9yeSUyMG1vZGVybnxlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Manufacturing Process"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-walnut-900/90 via-walnut-800/80 to-walnut-600/70 dark:from-black/90 dark:via-walnut-900/85 dark:to-walnut-800/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {t("manufacturing.hero.title")}
            </h1>
            <p className="text-xl text-walnut-100 max-w-3xl mx-auto">
              {t("manufacturing.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              5-Step Process
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-2xl mx-auto">
              A streamlined approach to delivering exceptional packaging
              solutions
            </p>
          </motion.div>

          <div className="space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <GlassCard className="p-8">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-walnut-500 to-walnut-700 flex items-center justify-center">
                        <step.icon className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-center mt-3">
                        <span className="text-2xl font-bold text-walnut-600 dark:text-walnut-400">
                          Step {index + 1}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-3xl font-bold text-walnut-900 dark:text-white mb-3">
                        {step.title}
                      </h3>
                      <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-6">
                        {step.description}
                      </p>
                      <ul className="grid md:grid-cols-2 gap-3">
                        {step.details.map((detail, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-walnut-600 dark:text-walnut-400 flex-shrink-0 mt-0.5" />
                            <span className="text-walnut-700 dark:text-walnut-300">
                              {detail}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Visual Timeline */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <GlassCard className="p-12">
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-8 text-center">
              Typical Project Timeline
            </h2>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-24 text-right font-semibold text-walnut-700 dark:text-walnut-300">
                  Week 1-2
                </div>
                <div className="h-2 flex-1 bg-gradient-to-r from-walnut-500 to-walnut-600 rounded-full" />
                <div className="flex-1 text-walnut-700 dark:text-walnut-300">
                  Design & Prototyping
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-24 text-right font-semibold text-walnut-700 dark:text-walnut-300">
                  Week 2-3
                </div>
                <div className="h-2 flex-1 bg-gradient-to-r from-walnut-500 to-walnut-600 rounded-full" />
                <div className="flex-1 text-walnut-700 dark:text-walnut-300">
                  Material Selection & Approval
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-24 text-right font-semibold text-walnut-700 dark:text-walnut-300">
                  Week 3-5
                </div>
                <div className="h-2 flex-1 bg-gradient-to-r from-walnut-500 to-walnut-600 rounded-full" />
                <div className="flex-1 text-walnut-700 dark:text-walnut-300">
                  Production
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-24 text-right font-semibold text-walnut-700 dark:text-walnut-300">
                  Week 5-6
                </div>
                <div className="h-2 flex-1 bg-gradient-to-r from-walnut-500 to-walnut-600 rounded-full" />
                <div className="flex-1 text-walnut-700 dark:text-walnut-300">
                  Quality Testing & Delivery
                </div>
              </div>
            </div>
            <p className="text-center text-walnut-600 dark:text-walnut-400 mt-8">
              *Timeline may vary based on order complexity and volume
            </p>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
